import { t } from '@utils/i18n-to-migrate/useTranslations'
import { Building2, HeartHandshake, type LucideProps, User } from 'lucide-react'
import type { ForwardRefExoticComponent, RefAttributes } from 'react'
import {
  type ForestOwnerSummaryModel,
  type ForestOwnerTypeModel,
  ForestOwnerTypeModelValues,
  type IdentificationNumberTypeModel,
  IdentificationNumberTypeValues,
} from '@/types/ForestOwners/ForestOwner'

//TODO: Refactor this file to use the new ForestOwnerTypeModel
// and remove the old ForestOwnerType and related constants
// and functions when the backend has been updated to return the new type values
export const ForestOwnerTypes = {
  private: 'private',
  organization: 'organization',
  partnership: 'partnership',
} as const

export type ForestOwnerType = (typeof ForestOwnerTypes)[keyof typeof ForestOwnerTypes]

export interface ForestOwnerTypeBadgeInfo {
  type: ForestOwnerType
  label: string
  variant: 'default' | 'success' | 'warning' | 'destructive' | 'informative'
}

/**
 * Checks if the identification number type is a social security number
 * @param idType - The identification number type
 * @returns True if it is an SSN, false otherwise
 */
export const isSocialSecurityNumberType = (
  idType: IdentificationNumberTypeModel | undefined,
): idType is 0 | 'Ssn' => {
  return idType === IdentificationNumberTypeValues.Ssn || idType === 'Ssn'
}

/**
 * Checks if the identification number type is an organization number
 * @param idType - The identification number type
 * @returns True if it is an organization number, false otherwise
 */
export const isOrganizationNumberType = (
  idType: IdentificationNumberTypeModel | undefined,
): idType is 1 | 'OrganizationNumber' => {
  return (
    idType === IdentificationNumberTypeValues.OrganizationNumber || idType === 'OrganizationNumber'
  )
}

const forestOwnerTypeMap: Partial<Record<ForestOwnerTypeModel, ForestOwnerType>> = {
  [ForestOwnerTypeModelValues.Private]: ForestOwnerTypes.private,
  Private: ForestOwnerTypes.private,
  [ForestOwnerTypeModelValues.Organization]: ForestOwnerTypes.organization,
  Organization: ForestOwnerTypes.organization,
  [ForestOwnerTypeModelValues.Partnership]: ForestOwnerTypes.partnership,
  Partnership: ForestOwnerTypes.partnership,
}

const forestOwnerTypeBadgeConfig: Record<
  ForestOwnerType,
  Omit<ForestOwnerTypeBadgeInfo, 'type'>
> = {
  [ForestOwnerTypes.private]: {
    label: t('forestowner.type.private'),
    variant: 'default',
  },
  [ForestOwnerTypes.partnership]: {
    label: t('forestowner.type.partnership'),
    variant: 'default',
  },
  [ForestOwnerTypes.organization]: {
    label: t('forestowner.type.organization'),
    variant: 'default',
  },
}

const forestOwnerTypeIcons: Record<
  ForestOwnerType,
  ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & RefAttributes<SVGSVGElement>>
> = {
  [ForestOwnerTypes.private]: User,
  [ForestOwnerTypes.partnership]: HeartHandshake,
  [ForestOwnerTypes.organization]: Building2,
}

/**
 * Determines the forest owner type based on the backend forestOwnerType property
 * @param forestOwner - The forest owner object
 * @returns The forest owner type
 */
export const getForestOwnerType = (
  forestOwner: Pick<ForestOwnerSummaryModel, 'forestOwnerType'> | undefined,
): ForestOwnerType =>
  forestOwner
    ? (forestOwnerTypeMap[forestOwner.forestOwnerType] ?? ForestOwnerTypes.private)
    : ForestOwnerTypes.private

/**
 * Gets badge information for displaying forest owner type
 * @param forestOwner - The forest owner object
 * @returns Badge information with type, label, and variant
 */
export const getForestOwnerTypeBadgeInfo = (
  forestOwner: Pick<ForestOwnerSummaryModel, 'forestOwnerType'> | undefined,
): ForestOwnerTypeBadgeInfo => {
  const type = getForestOwnerType(forestOwner)

  return {
    type,
    ...forestOwnerTypeBadgeConfig[type],
  }
}

/**
 * Gets the appropriate icon component for each forest owner type
 * @param type - The forest owner type
 * @returns The corresponding Lucide icon component
 */
export const getForestOwnerTypeIcon = (
  type: ForestOwnerType,
): ForwardRefExoticComponent<Omit<LucideProps, 'ref'> & RefAttributes<SVGSVGElement>> => {
  return forestOwnerTypeIcons[type]
}
